import React from 'react';
import {
  Bell,
  FileText,
  ShoppingCart,
  Package,
  UserSquare,
  ClipboardList,
  Receipt,
  Inbox,
  X,
} from 'lucide-react';

const ACTIVITY_ICON = {
  invoice: FileText,
  quotation: ClipboardList,
  proforma: Receipt,
  credit_note: Receipt,
  purchase_order: ShoppingCart,
  purchase_invoice: Receipt,
  customer: UserSquare,
  vendor: UserSquare,
  product: Package,
  payment: Receipt,
};

const ACTIVITY_VERB = {
  invoice: 'New invoice',
  quotation: 'New quotation',
  proforma: 'New proforma',
  credit_note: 'New credit note',
  purchase_order: 'New purchase order',
  purchase_invoice: 'New purchase invoice',
  customer: 'Customer added',
  vendor: 'Vendor added',
  product: 'Product added',
  payment: 'Payment recorded',
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Activity bell — tenant-scoped events from the trailing 24 hours.
 * Pure presentational; lifecycle (fetch + open/close) lives in TopBar.
 */
export const ActivityMenu = React.forwardRef(function ActivityMenu(
  { open, onToggle, items, unreadCount, onDismiss, getId = (a, i) => `${a?.type || 'x'}-${a?.id || i}` },
  ref,
) {
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        title="Recent activity (last 24h)"
        data-testid="topbar-activity-btn"
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        style={{ color: 'var(--theme-primary)' }}
      >
        <Bell className="w-4 h-4" strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span
            data-testid="topbar-activity-badge"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-800"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1.5 z-50"
          data-testid="topbar-activity-menu"
        >
          <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
            <div>
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Recent activity</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Events from the last 24 hours</div>
            </div>
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200">
              {items.length}
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Inbox className="w-8 h-8 text-slate-300 dark:text-slate-500 mb-2" />
                <div className="text-sm text-slate-500 dark:text-slate-400">No activity in the last 24 hours</div>
              </div>
            ) : (
              items.map((a, idx) => {
                const Icon = ACTIVITY_ICON[a.type] || FileText;
                const id = getId(a, idx);
                return (
                  <div
                    key={id}
                    className="group relative px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 border-b border-slate-50 dark:border-slate-700 last:border-b-0"
                    data-testid={`topbar-activity-item-${a.type}`}
                  >
                    <div className="flex items-start gap-2.5 pr-6">
                      <div className="mt-0.5 w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-3.5 h-3.5 text-slate-600 dark:text-slate-200" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-slate-700 dark:text-slate-200 leading-snug">
                          <span className="font-semibold">{ACTIVITY_VERB[a.type] || 'Activity'}</span>
                          {' — '}
                          <span className="text-slate-600 dark:text-slate-300">{a.title}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                          <span>{timeAgo(a.created_at)}</span>
                          {a.created_by_name && (
                            <>
                              <span>•</span>
                              <span>by {a.created_by_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDismiss?.(id); }}
                      aria-label="Dismiss notification"
                      title="Dismiss"
                      data-testid={`topbar-activity-dismiss-${a.type}`}
                      className="absolute top-1.5 right-1.5 h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 dark:hover:bg-slate-600/60 dark:hover:text-slate-100 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default ActivityMenu;
